import { ESPLoader, Transport } from 'esptool-js';
import intel_hex from 'intel-hex';

async function resetViaDTR(port: SerialPort, appendLog: (message: string) => void): Promise<boolean> {
    try {
        await port.close().catch(() => {});
        try {
            await port.open({ baudRate: 1200 });
            await port.setSignals({ dataTerminalReady: false });
            await new Promise((resolve) => setTimeout(resolve, 200));
            await port.close();
            await new Promise((resolve) => setTimeout(resolve, 500));
        } catch (error) {
            await port.close().catch(() => {});
        }
        try {
            await port.open({ baudRate: 115200 });
            await port.close();
        } catch (error) {}
        await new Promise((resolve) => setTimeout(resolve, 500));
        return true;
    } catch (error) {
        return false;
    }
}

class Stk500Client {
    port: SerialPort;
    reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
    writer: WritableStreamDefaultWriter<Uint8Array> | null = null;
    buffer: number[] = [];
    isReading = false;

    constructor(port: SerialPort) {
        this.port = port;
    }

    async connect() {
        this.writer = this.port.writable.getWriter();
        this.reader = this.port.readable.getReader();
        this.isReading = true;
        this.readLoop();
    }

    async readLoop() {
        try {
            while (this.isReading && this.reader) {
                const { value, done } = await this.reader.read();
                if (done) break;
                if (value) {
                    for (let i = 0; i < value.length; i++) {
                        this.buffer.push(value[i]);
                    }
                }
            }
        } catch (e) {
            this.isReading = false;
        }
    }

    async readBytes(length: number, timeoutMs = 1000): Promise<number[] | null> {
        const start = Date.now();
        while (this.buffer.length < length) {
            if (Date.now() - start > timeoutMs) {
                return null;
            }
            await new Promise((r) => setTimeout(r, 5));
        }
        return this.buffer.splice(0, length);
    }

    async sendCommand(cmd: number[]): Promise<void> {
        await this.writer!.write(new Uint8Array(cmd));
    }

    async sync() {
        for (let i = 0; i < 10; i++) {
            try {
                this.buffer = [];
                await this.sendCommand([0x30, 0x20]);
                const resp = await this.readBytes(2, 200);
                if (resp && resp[0] === 0x14 && resp[1] === 0x10) return true;
            } catch (e) {
                return false;
            }
        }
        return true;
    }

    async enterProgMode() {
        await this.sendCommand([0x50, 0x20]);
        const resp = await this.readBytes(2);
        if (!resp || resp[0] !== 0x14 || resp[1] !== 0x10) {
            return false;
        }
        return true;
    }

    async leaveProgMode() {
        await this.sendCommand([0x51, 0x20]);
        await this.readBytes(2);
    }

    async loadAddress(addr: number) {
        const wordAddr = Math.floor(addr / 2);
        await this.sendCommand([0x55, wordAddr & 0xff, (wordAddr >> 8) & 0xff, 0x20]);
        const resp = await this.readBytes(2);
        if (!resp || resp[0] !== 0x14 || resp[1] !== 0x10) {
            return false;
        }
        return true;
    }

    async writePage(data: Uint8Array) {
        const cmd = [0x64, (data.length >> 8) & 0xff, data.length & 0xff, 0x46];
        const fullCmd = new Uint8Array(cmd.length + data.length + 1);
        fullCmd.set(cmd, 0);
        fullCmd.set(data, cmd.length);
        fullCmd[fullCmd.length - 1] = 0x20;

        await this.writer!.write(fullCmd);
        const resp = await this.readBytes(2);
        if (!resp || resp[0] !== 0x14 || resp[1] !== 0x10) {
            return false;
        }
        return true;
    }

    async readPage(length: number) {
        const cmd = [0x74, (length >> 8) & 0xff, length & 0xff, 0x46, 0x20];
        await this.writer!.write(new Uint8Array(cmd));

        const resp = await this.readBytes(1);
        if (!resp || resp[0] !== 0x14) return null;

        const data = await this.readBytes(length, 2000);
        if (!data) return null;

        const sync = await this.readBytes(1);
        if (!sync || sync[0] !== 0x10) return null;

        return data;
    }

    async disconnect() {
        this.isReading = false;
        if (this.reader) await this.reader.cancel().catch(() => {});
        if (this.writer) {
            this.writer.releaseLock();
            this.writer = null;
        }
        if (this.reader) {
            this.reader.releaseLock();
            this.reader = null;
        }
        await this.port.close().catch(() => {});
    }

    async uploadFirmware(binaryData: Uint8Array, skipVerify: boolean, pageSize = 128): Promise<boolean> {
        this.buffer = [];
        if (!(await this.sync())) return false;
        if (!(await this.enterProgMode())) return false;
        for (let offset = 0; offset < binaryData.length; offset += pageSize) {
            const slice = binaryData.slice(offset, offset + pageSize);
            const pageData = new Uint8Array(pageSize);
            pageData.fill(0xff);
            pageData.set(slice);
            if (!(await this.loadAddress(offset))) return false;
            if (!(await this.writePage(pageData))) return false;
        }
        if (!skipVerify) {
            for (let offset = 0; offset < binaryData.length; offset += pageSize) {
                const slice = binaryData.slice(offset, offset + pageSize);
                const expectedData = new Uint8Array(pageSize);
                expectedData.fill(0xff);
                expectedData.set(slice);

                if (!(await this.loadAddress(offset))) return false;
                const readData = await this.readPage(pageSize);
                if (!readData) return false;

                for (let i = 0; i < pageSize; i++) {
                    if (readData[i] !== expectedData[i]) {
                        return false;
                    }
                }
            }
        }
        await this.leaveProgMode();
        return true;
    }
}

interface OrangeUploadOptions {
    port: SerialPort;
    binaryData: Uint8Array;
    appendLog: (message: string) => void;
    onUploadEvent: (e: {
        source: 'webserialapi';
        kind: 'progress' | 'result';
        progress?: number;
        phase?: 'sending' | 'writing';
        message?: string;
        success?: boolean;
        errorMessage?: string;
    }) => void;
}

async function OrangeUpload({ port, binaryData, appendLog, onUploadEvent }: OrangeUploadOptions): Promise<void> {
    let client: Stk500Client | null = null;
    const MAX_RETRY = 2;
    for (let attempt = 0; attempt < MAX_RETRY; attempt++) {
        try {
            if (attempt === 0) {
                onUploadEvent({
                    source: 'webserialapi',
                    kind: 'progress',
                    progress: 60,
                    phase: 'sending',
                    message: '書き込み準備中...',
                });
            } else {
                appendLog(`[serial] リトライ ${attempt}/${MAX_RETRY - 1}`);
                onUploadEvent({
                    source: 'webserialapi',
                    kind: 'progress',
                    progress: 60,
                    phase: 'sending',
                    message: `リトライ中... (${attempt}/${MAX_RETRY - 1})`,
                });
            }
            await resetViaDTR(port, appendLog);
            await port.close().catch(() => {});
            await port.open({ baudRate: 115200 });
            await port.setSignals({ dataTerminalReady: false });
            await new Promise((r) => setTimeout(r, 50));
            await port.setSignals({ dataTerminalReady: true });
            await new Promise((r) => setTimeout(r, 50));

            const skipVerify = attempt > 0;
            client = new Stk500Client(port);
            await client.connect();
            onUploadEvent({
                source: 'webserialapi',
                kind: 'progress',
                progress: 80,
                phase: 'writing',
                message: '書き込み中...',
            });
            let success = await client.uploadFirmware(binaryData, skipVerify);
            if (success) {
                appendLog('🎉スクーミーボードに書き込みが完了しました！');
                onUploadEvent({
                    source: 'webserialapi',
                    kind: 'result',
                    success: true,
                    message: '書き込み完了！',
                });
                return;
            } else {
                if (attempt === MAX_RETRY - 1) {
                    appendLog(`☔スクーミーボードへの書き込みに失敗しました。`);
                    onUploadEvent({
                        source: 'webserialapi',
                        kind: 'result',
                        success: false,
                        errorMessage: 'エラー',
                    });
                } else {
                    await new Promise((r) => setTimeout(r, 1000));
                }
            }
        } catch (error: unknown) {
            if (attempt === MAX_RETRY - 1) {
                appendLog(`☔スクーミーボードへの書き込みに失敗しました。`);
                const errorMessage =
                    error instanceof Error || error instanceof DOMException ? error.message : String(error);
                onUploadEvent({
                    source: 'webserialapi',
                    kind: 'result',
                    success: false,
                    errorMessage: errorMessage || 'エラー',
                });
            } else {
                await new Promise((r) => setTimeout(r, 1000));
            }
        } finally {
            if (client) {
                await client.disconnect().catch(() => {});
                client = null;
            }
        }
    }
}

interface BlueUploadOptions {
    port: SerialPort;
    binaryData: Uint8Array;
    esp32Artifacts: Array<{ filename: string; address: string; data_b64: string }> | null;
    appendLog: (message: string) => void;
    onUploadEvent: (e: {
        source: 'webserialapi';
        kind: 'progress' | 'result';
        progress?: number;
        phase?: 'sending' | 'writing';
        message?: string;
        success?: boolean;
        errorMessage?: string;
    }) => void;
}

async function BlueUpload({
    port,
    binaryData,
    esp32Artifacts,
    appendLog,
    onUploadEvent,
}: BlueUploadOptions): Promise<void> {
    let transport: Transport | null = null;
    let esploader: ESPLoader | null = null;

    try {
        await port.close().catch(() => {});
        const terminal = {
            clean() {},
            writeLine(data: string) {},
            write(data: string) {},
        };

        transport = new Transport(port, true);
        const loaderOptions = {
            transport,
            baudrate: 921600,
            terminal: terminal,
            romBaudrate: 115200,
        };

        esploader = new ESPLoader(loaderOptions);
        onUploadEvent({
            source: 'webserialapi',
            kind: 'progress',
            progress: 60,
            phase: 'sending',
            message: '書き込み準備中...',
        });
        await esploader.main();

        let fileArrayData: Array<{ data: string; address: number }>;

        if (esp32Artifacts && esp32Artifacts.length > 0) {
            // 複数ファイル書き分け: bootloader(0x1000) + partitions(0x8000) + boot_app0(0xe000) + app(0x10000)
            fileArrayData = esp32Artifacts.map((artifact) => {
                const binaryString = window.atob(artifact.data_b64);
                let binStr = '';
                for (let i = 0; i < binaryString.length; i++) {
                    binStr += binaryString[i];
                }
                return {
                    data: binStr,
                    address: parseInt(artifact.address, 16),
                };
            });
        } else {
            // フォールバック
            let binString = '';
            for (let i = 0; i < binaryData.length; i++) {
                binString += String.fromCharCode(binaryData[i]);
            }
            fileArrayData = [{ data: binString, address: 0x0 }];
        }

        onUploadEvent({
            source: 'webserialapi',
            kind: 'progress',
            progress: 80,
            phase: 'writing',
            message: '書き込み中...',
        });
        await esploader.writeFlash({
            fileArray: fileArrayData,
            flashSize: '4MB',
            flashMode: 'dio',
            flashFreq: '80m',
            eraseAll: false,
            compress: true,
        });

        appendLog('🎉スクーミーボードに書き込みが完了しました！');
        onUploadEvent({
            source: 'webserialapi',
            kind: 'result',
            success: true,
            message: '書き込み完了！',
        });
        await transport.setDTR(false);
        await transport.setRTS(true);
        await new Promise((r) => setTimeout(r, 100));
        await transport.setRTS(false);
    } catch (error: unknown) {
        appendLog(`☔スクーミーボードへの書き込みに失敗しました。`);
        const errorMessage = error instanceof Error || error instanceof DOMException ? error.message : String(error);
        onUploadEvent({
            source: 'webserialapi',
            kind: 'result',
            success: false,
            errorMessage: errorMessage || 'エラー',
        });
    } finally {
        if (transport) {
            await transport.disconnect().catch(() => {});
        }
    }
}

interface WebSerialApiUploadOptions {
    board: 'orange' | 'blue';
    port: SerialPort;
    firmwareData: string;
    firmwareDataType: string;
    esp32Artifacts?: Array<{ filename: string; address: string; data_b64: string }> | null;
    appendLog: (message: string) => void;
    onUploadEvent: (e: {
        source: 'webserialapi';
        kind: 'progress' | 'result';
        progress?: number;
        phase?: 'sending' | 'writing';
        message?: string;
        success?: boolean;
        errorMessage?: string;
    }) => void;
}

export async function WebSerialApiUpload({
    board,
    port,
    firmwareData,
    firmwareDataType,
    esp32Artifacts,
    appendLog,
    onUploadEvent,
}: WebSerialApiUploadOptions): Promise<void> {
    if (!port) {
        appendLog('ポートが接続されていません');
        return;
    }
    await new Promise((resolve) => setTimeout(resolve, 500));

    let binaryData: Uint8Array;
    if (firmwareDataType === 'hex') {
        const parsed = intel_hex.parse(firmwareData);
        binaryData = new Uint8Array(parsed.data);
    } else if (firmwareDataType === 'bin') {
        const binaryString = window.atob(firmwareData);
        binaryData = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            binaryData[i] = binaryString.charCodeAt(i);
        }
    } else {
        appendLog(`不明なファームウェアタイプ: ${firmwareDataType}`);
        return;
    }

    if (board === 'orange') {
        await OrangeUpload({ port, binaryData, appendLog, onUploadEvent });
    } else if (board === 'blue') {
        await BlueUpload({ port, binaryData, esp32Artifacts: esp32Artifacts ?? null, appendLog, onUploadEvent });
    } else {
        appendLog(`不明なボードタイプ: ${board}`);
    }
}
